/**
 * AliasesInput Component
 *
 * Tag-style input for managing client aliases.
 * Supports adding aliases via Enter key and removing via click.
 */

import { useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface AliasesInputProps {
  value: string[];
  onChange: (aliases: string[]) => void;
  placeholder?: string;
  maxAliases?: number;
  disabled?: boolean;
  className?: string;
}

export function AliasesInput({
  value,
  onChange,
  placeholder = 'Add alias and press Enter',
  maxAliases = 20,
  disabled = false,
  className,
}: AliasesInputProps) {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const trimmed = inputValue.trim();
        if (trimmed && !value.includes(trimmed) && value.length < maxAliases) {
          onChange([...value, trimmed]);
          setInputValue('');
        }
      } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
        // Remove last alias when backspace is pressed on empty input
        onChange(value.slice(0, -1));
      }
    },
    [inputValue, value, onChange, maxAliases],
  );

  const handleRemove = useCallback(
    (alias: string) => {
      onChange(value.filter((a) => a !== alias));
    },
    [value, onChange],
  );

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap gap-1.5">
        {value.map((alias) => (
          <Badge key={alias} variant="secondary" className="gap-1 pr-1">
            {alias}
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemove(alias)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
                aria-label={`Remove ${alias}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={
          value.length >= maxAliases ? 'Maximum aliases reached' : placeholder
        }
        disabled={disabled || value.length >= maxAliases}
        className="h-9"
      />
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {value.length} alias{value.length !== 1 ? 'es' : ''} added
        </p>
      )}
    </div>
  );
}
